import React from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { FaCheck } from "react-icons/fa";
import { CreateBusinessForm } from "../../../../types/forms";
import useAuth from "../../../../hooks/account/useAuth";
import { BusinessType } from "../../../../types/business";
import { getMinimumBusinessSubscriptionForFeatures } from "../../../../utils/BusinessFeatureUtils";
import { BusinessSubscriptionType } from "../../../../types/misc";
import { StepFlowStep } from "../../../components/misc/StepFlow";
import IconBadge from "../../../components/icons/IconBadge";
import { businessTypeIcons } from "../../../../utils/iconUtils";

type BusinessSetupViewProps = {
  setStep: (step: 1 | -1) => void;
  business: CreateBusinessForm;
  setBusiness: (business: CreateBusinessForm) => void;
}

export default function BusinessSetupView({ business, setBusiness, setStep }: BusinessSetupViewProps) {
  const { t } = useTranslation();
  const auth = useAuth();

  const typeOptions = (
    Object.keys(BusinessType)
  ) as BusinessType[];

  const getDefaultFeatures = (businessType: BusinessType) => {
    switch (businessType) {
      case BusinessType.SPORTCLUB:
      case BusinessType.STUDENTASSOCIATION:
      case BusinessType.OTHER_BUSINESS:
        return {
          EASY_SHARING: false,
          MEMBER_ADMINISTRATION: true,
          EVENTS: true,
          PARTICIPATION_INSIGHTS: true,
          TASKS: false,
          STATS_AND_SCORES: false,
          COMMUNICATION: true,
          CHATS: false,
          COST_SPLITTING: true,
          FINANCIAL_ADMINISTRATION: true,
          PHOTO_ALBUMS: true,
        };
      default:
        return {};
    }
  };

  const handleTypeSelection = (type: BusinessType) => {
    const isDeselecting = type === business.type;
    const newBusinessType = isDeselecting ? undefined : type;
    const newFeatures = newBusinessType ? getDefaultFeatures(newBusinessType) : undefined;
    const requiredSubscription = newFeatures
      ? getMinimumBusinessSubscriptionForFeatures(newFeatures)
      : BusinessSubscriptionType.STARTER;

    setBusiness({
      ...business,
      type: newBusinessType,
      dashboardFeatures: newFeatures,
      connectedMembers: newFeatures ? newFeatures.EASY_SHARING : false,
      subscriptionType: requiredSubscription,
    });
  };

  return (
    <StepFlowStep
      setStep={setStep}
      disabled={!business.type || !business.name}
      showPrev={!auth}
      title={t('page.onboarding.business.type.title')}
      subtitle={t(`page.onboarding.business.type.subtitle`)}
      className="max-w-4xl">
      <div className="flex flex-col gap-6 pr-2">
        <div className="relative flex w-1/2 flex-shrink-0 flex-col gap-1">
          <div className="hidden items-start justify-between gap-1 sm:flex">
            <p className="font-semibold">{t(`page.onboarding.business.info.name`)}</p>
          </div>
          <div className="relative w-full">
            <div className="h-10 sm:hidden"> </div>
            <input
              type="text"
              className="w-full"
              placeholder={`${t('general.name')}...`}
              value={business.name || ''}
              onChange={(e) => setBusiness({ ...business, name: e.target.value })}
            />
            <p className="absolute right-4 top-2 text-sm sm:hidden">{t('general.mandatory')}</p>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <p className="font-semibold">{t('page.onboarding.business.info.type')}</p>
          <div
            className="grid gap-3 lg:gap-4"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}>
            {typeOptions.map((type) => (
              <div
                key={type}
                onClick={() => handleTypeSelection(type)}
                className={classNames(
                  'group relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-300 hover:shadow-md',
                  {
                    'border-primary-900 bg-opacity-10 shadow-sm': business.type === type,
                    'border-secondary-200 bg-secondary-200 bg-opacity-30 hover:border-primary-900 hover:bg-opacity-40':
                      business.type !== type,
                  },
                )}>
                <div
                  className={classNames(
                    'absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full border-2 transition-all duration-200',
                    {
                      'border-primary-900 bg-primary-900': business.type === type,
                      'border-secondary-200 group-hover:border-primary-900':
                        business.type !== type,
                    },
                  )}>
                  {business.type === type && (
                    <FaCheck className="h-2.5 w-2.5 text-secondary-50" />
                  )}
                </div>

                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg transition-all duration-200">
                  <IconBadge
                    className={classNames({
                      'bg-primary-900 text-secondary-200': business.type === type,
                      'bg-secondary-200 text-primary-900': business.type !== type,
                    })}
                    icon={businessTypeIcons[type]}
                  />
                </div>

                <div className="space-y-1">
                  <h3 className="text-base font-semibold text-primary-900">
                    {t(`page.onboarding.business.type.${type.toLowerCase().split('_')[0]}.name`)}
                  </h3>
                  <p className="text-xs leading-relaxed text-primary-900 opacity-75">
                    {t(`page.onboarding.business.type.${type.toLowerCase().split('_')[0]}.description`)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </StepFlowStep>
  );
}
